import {
  Component,
  HostListener,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { NgProgress } from '@ngx-progressbar/core';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import {
  FileRestrictions,
  FileState,
  SelectEvent,
  UploadComponent
} from '@progress/kendo-angular-upload';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import * as XLSX from 'xlsx';
import { APIOperableComponent } from '../../../../shared/components/api-operable/api-operable.component';
import { ColorCodeModel } from '../../../../shared/model/color-code-model';
import { ExportToExcelService } from '../../../../shared/services/export-to-excel.service';
import {
  UIActiveDialogService,
  UIDialogService
} from '../../../../shared/services/ui-dialog.service';
import { AuthService } from '../../../security/service/auth.service';
import { ProductWarrantyUpsertArrayModel } from '../../model/products/bulk-add-warranty-array.model';
import { BulkAddUIMessages } from '../../model/products/bulk-add-warranty-ui-messages';
import { ProductWarrantyUpsertModel } from '../../model/products/bulk-add-warranty.model';
import { BulkAddProductsService } from '../../service/bulk-add-products.service';
import { BulkAddProgressDialog } from '../../shared/bulk-add-progress-dialog/bulk-add-progress-dialog.component';
import { PleaseCheckData } from '../../shared/please-check-grid-dialog/please-check-grid-dialogue.component';

@Component({
  selector: 'product-warranty',
  encapsulation: ViewEncapsulation.None,
  templateUrl:
    'console/app-js/modules/bulkAdd/components/product-warranty/product-warranty.component.html'
})
export class ProductWarrantyComponent extends APIOperableComponent {
  protected data: any = [];
  protected numWarrantys: number;
  protected errorCountSubmit = 0;
  protected indexOfRecordsSent = 1;
  protected failedRows = '';
  protected errorArr: any = [];
  private errorArrFront: any = [];
  protected gridView: GridDataResult;
  protected pageSize = 10;
  protected skip = 0;
  public fileState = FileState;
  private warrantyObservables: Observable<any>[] = [];

  private BATCH_REQUEST_NUMBER = 50;
  private EXCEL_FILE_NAME = 'BulkWarrantyDurationError.xlsx';

  // block all other file types other than .xlsx files
  public uploadRestrictions: FileRestrictions = {
    allowedExtensions: ['.xlsx']
  };
  protected templateText = 'Warranty Duration Upload Template';

  private updatedItems: any = [];
  private createdItems: any = [];
  private recordsSent = 0;
  private errorTrys = 0;

  @ViewChild('fileUpload', { static: true }) fileUpload: UploadComponent;

  constructor(
    private exportToExcelService: ExportToExcelService,
    private bulkAddProductsService: BulkAddProductsService,
    private dialogOpenService: UIDialogService,
    private dialogCloseService: UIActiveDialogService,
    private sanitizer: DomSanitizer,
    toastr: ToastrService,
    progressService: NgProgress,
    authService: AuthService,
    private formBuilder: FormBuilder
  ) {
    super(progressService, toastr, authService);
  }

  @HostListener('window:beforeunload')
  canDeactivate() {
    return this.ableToNavigateAway();
  }

  ableToNavigateAway(): boolean {
    if (this.updatedItems.length > 0) {
      return false;
    } else {
      return true;
    }
  }

  // Kendo UI Upload select event, when a file is selected for upload
  public selectEventHandler(event: SelectEvent): void {
    // testing
    const self = this;
    self.resetErrorGrid();

    event.files.forEach((file: any) => {
      if (!file.validationErrors) {
        const reader: FileReader = new FileReader();

        reader.onload = function(e: any) {
          /* read workbook */
          /* with base64 have to replace the beginning part of the string that tells what format it is in in order for it to decode properly */
          const bstr: string = e.target.result.replace(
            /^data:(.*?);base64,/,
            ''
          );
          const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'base64' });

          /* grab first sheet */
          const wsname: string = wb.SheetNames[0];
          const ws: XLSX.WorkSheet = wb.Sheets[wsname];

          /* save data */
          self.data = XLSX.utils.sheet_to_json(ws, { blankrows: false });
          self.bulkAddWarrantyValidation(self.data);
        };

        reader.readAsDataURL(file.rawFile);
      } else {
        console.error('Error encountered in selectEventHandler.');
      }
    });
  }

  // Reset grid between uploads so that the grid of errors is only for the current upload
  private resetErrorGrid(): void {
    const self = this;

    self.gridView = {
      data: null,
      total: 0
    };

    // reset the grid page to zero
    self.skip = 0;

    self.errorArr = [];
    self.errorArrFront = [];
    self.numWarrantys = 0;
    self.failedRows = '';
  }

  private rowValidation(warranty: ProductWarrantyUpsertModel): any {
    let colArr = [];

    if (
      warranty.MachineProductId === undefined ||
      warranty.MachineProductId === 0
    ) {
      warranty.Issues =
        warranty.Issues === undefined
          ? BulkAddUIMessages.MachineProductId
          : warranty.Issues + '; ' + BulkAddUIMessages.MachineProductId;
      colArr.push('machineProductId');
    } else if (
      warranty.MachineProductId !== undefined &&
      isNaN(warranty.MachineProductId)
    ) {
      warranty.Issues =
        warranty.Issues === undefined
          ? BulkAddUIMessages.MachineProductIdInvalid
          : warranty.Issues + '; ' + BulkAddUIMessages.MachineProductIdInvalid;
      colArr.push('machineProductId');
    }

    if (
      warranty.LaborWarrantyDays !== undefined &&
      (isNaN(warranty.LaborWarrantyDays) || warranty.LaborWarrantyDays < 0)
    ) {
      warranty.Issues =
        warranty.Issues === undefined
          ? BulkAddUIMessages.InvalidLaborWarranty
          : warranty.Issues + '; ' + BulkAddUIMessages.InvalidLaborWarranty;
      colArr.push('laborWarrantyDays');
    }

    if (
      warranty.PartWarrantyDays !== undefined &&
      (isNaN(warranty.PartWarrantyDays) || warranty.PartWarrantyDays < 0)
    ) {
      warranty.Issues =
        warranty.Issues === undefined
          ? BulkAddUIMessages.InvalidPartWarranty
          : warranty.Issues + '; ' + BulkAddUIMessages.InvalidPartWarranty;
      colArr.push('partWarrantyDays');
    }

    if (
      warranty.PartWarrantyDays !== undefined &&
      warranty.LaborWarrantyDays !== undefined &&
      warranty.PartWarrantyDays == 0 &&
      warranty.LaborWarrantyDays == 0
    ) {
      warranty.Issues =
        warranty.Issues === undefined
          ? BulkAddUIMessages.InvalidLaborOrPartWarranty
          : warranty.Issues +
            '; ' +
            BulkAddUIMessages.InvalidLaborOrPartWarranty;
      colArr.push('laborWarrantyDays');
      colArr.push('partWarrantyDays');
    }

    if (
      warranty.LaborWarrantyDays === undefined &&
      warranty.PartWarrantyDays === undefined
    ) {
      warranty.Issues =
        warranty.Issues === undefined
          ? BulkAddUIMessages.InvalidLaborOrPartWarranty
          : warranty.Issues +
            '; ' +
            BulkAddUIMessages.InvalidLaborOrPartWarranty;
      colArr.push('laborWarrantyDays');
      colArr.push('partWarrantyDays');
    }

    if (colArr.length > 0) {
      warranty.Column = colArr;
      this.errorArrFront.push(warranty);
      return true;
    } else {
      delete warranty.Issues;
      delete warranty.Column;
      return warranty;
    }
  }

  private formatBadErrors(res): void {
    let badWarrantyArray = [];
    let loadedRecords = 0;
    for (let obs of res) {
      loadedRecords += obs.loadedRecords;
      badWarrantyArray = badWarrantyArray.concat(obs.badProductsWarranty);
    }
    badWarrantyArray.forEach(badWarranty => {
      Object.keys(badWarranty.value)
        .map(key => badWarranty.value[key])
        .forEach(val => {
          if (badWarranty.key.Issues === undefined) {
            badWarranty.key.Issues = val;
          } else {
            badWarranty.key.Issues = badWarranty.key.Issues + ';' + val;
          }
        });
      badWarranty.key.Column = Object.keys(badWarranty.value);
      this.errorArr.push(
        this.mapToUpsertProductWarrantyModelBE(badWarranty.key)
      );
    });
    this.numWarrantys = loadedRecords;
    if (this.numWarrantys === this.recordsSent) {
      this.onAPISuccess(
        `${this.recordsSent} Product Warrantys Uploaded Succesfully`
      );
    } else if (this.errorArr.length > 0) {
      this.onAPIError(
        `${this.errorArr.length +
          this.errorArrFront.length} Product Warrantys with errors`
      );
    }
    this.dialogCloseService.close(BulkAddProgressDialog);
    this.errorArr = this.errorArr.concat(this.errorArrFront);
    this.loadItems();
  }

  private bulkAddWarrantyValidation(
    productWarrantys: ProductWarrantyUpsertModel[]
  ): void {
    this.recordsSent = productWarrantys.length;
    let upsertProductWarrantys = new ProductWarrantyUpsertArrayModel();
    upsertProductWarrantys.productWarrantyArray = [];
    let warrantyUpsertArrayModelArr: ProductWarrantyUpsertArrayModel[] = [];
    this.dialogOpenService.open(BulkAddProgressDialog, []);
    for (let x = 1; x <= productWarrantys.length; x++) {
      let productWarranty = this.mapToUpsertProductWarrantyModelCS(
        productWarrantys[x - 1]
      );
      productWarranty.Issues = undefined;
      let WarrantyUpload = this.rowValidation(productWarranty);
      if (WarrantyUpload !== true) {
        upsertProductWarrantys.productWarrantyArray.push(productWarranty);
      }
      if (
        x % this.BATCH_REQUEST_NUMBER === 0 ||
        productWarrantys.length - x === 0
      ) {
        warrantyUpsertArrayModelArr.push(
          Object.assign({}, upsertProductWarrantys)
        );
        upsertProductWarrantys.productWarrantyArray = [];
      }
    }
    this.concurrentUpload(warrantyUpsertArrayModelArr);
  }

  private mapToUpsertProductWarrantyModelBE(
    badProductWarranty: any
  ): ProductWarrantyUpsertModel {
    let badWarrantyUpsert = new ProductWarrantyUpsertModel();
    badWarrantyUpsert.MachineProductId = badProductWarranty.machineProductId;
    badWarrantyUpsert.LaborWarrantyDays = badProductWarranty.laborWarrantyDays;
    badWarrantyUpsert.PartWarrantyDays = badProductWarranty.partWarrantyDays;
    badWarrantyUpsert.Issues = badProductWarranty.Issues;
    badWarrantyUpsert.Column = badProductWarranty.Column;
    return badWarrantyUpsert;
  }

  private mapToUpsertProductWarrantyModelCS(
    ProductWarranty: ProductWarrantyUpsertModel
  ): ProductWarrantyUpsertModel {
    ProductWarranty = this.trimObj(ProductWarranty);
    let productWarrantyUpsert = new ProductWarrantyUpsertModel();
    productWarrantyUpsert.MachineProductId = ProductWarranty.MachineProductId;
    productWarrantyUpsert.LaborWarrantyDays = ProductWarranty.LaborWarrantyDays;
    productWarrantyUpsert.PartWarrantyDays = ProductWarranty.PartWarrantyDays;
    productWarrantyUpsert.Issues = ProductWarranty.Issues;
    productWarrantyUpsert.Column = [];
    return productWarrantyUpsert;
  }

  private updateSessionDuringBulkUpload(): void {
    let sessionGauge = 60 * 60 * 1500;
    let expiresTime = localStorage.expires_at;
    let currentTime = Date.now();
    if (expiresTime - currentTime <= sessionGauge) {
      this.authService.getConfigAndInitAuth0().subscribe(() => {
        this.authService.renewToken();
      });
    }
  }

  private trimObj(o: any): any {
    return JSON.parse(JSON.stringify(o).replace(/"\s+|\s+"/g, '"'));
  }

  private addAPITimeoutErrorMessage(
    productWarrantys: ProductWarrantyUpsertArrayModel
  ) {
    for (let i = 0; i < productWarrantys.productWarrantyArray.length; i++) {
      productWarrantys.productWarrantyArray[i].Issues =
        'API error, Please resubmit record';
      productWarrantys.productWarrantyArray[i].Column = ['machineProductId'];
      this.errorArrFront.push(
        this.mapToUpsertProductWarrantyModelCS(
          productWarrantys.productWarrantyArray[i]
        )
      );
    }
  }

  private concurrentUpload(
    upsertProductWarrantysArray: ProductWarrantyUpsertArrayModel[]
  ): void {
    this.bulkAddProductsService
      .productWarranty(upsertProductWarrantysArray[0])
      .subscribe(
        (res: any) => {
          // add results to warrantyObservables
          this.warrantyObservables.push(res);
        },
        err => {
          if (upsertProductWarrantysArray.length === 0) {
            this.dialogCloseService.close(BulkAddProgressDialog);
            this.formatBadErrors(this.warrantyObservables);
            this.warrantyObservables = [];
            this.onAPIError('Error Uploading Product Warrantys');
          } else {
            if (this.errorTrys < 2) {
              this.errorTrys++;
              this.concurrentUpload(upsertProductWarrantysArray);
            } else {
              this.errorTrys = 0;
              this.addAPITimeoutErrorMessage(upsertProductWarrantysArray[0]);
              upsertProductWarrantysArray = upsertProductWarrantysArray.slice(
                1
              );
              this.concurrentUpload(upsertProductWarrantysArray);
            }
          }
        },
        () => {
          this.errorTrys = 0;
          upsertProductWarrantysArray = upsertProductWarrantysArray.slice(1);
          if (upsertProductWarrantysArray.length === 0) {
            this.formatBadErrors(this.warrantyObservables);
            this.warrantyObservables = [];
          } else {
            this.concurrentUpload(upsertProductWarrantysArray);
          }
        }
      );
  }

  protected pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.loadItems();
  }

  private loadItems(): void {
    this.gridView = {
      data: this.errorArr.slice(this.skip, this.skip + this.pageSize),
      total: this.errorArr.length
    };
  }

  public colorCode(
    record: string[],
    index: number,
    dataFromRow: { ColumnsEdited: string }
  ): SafeStyle {
    let result: string;
    for (let col of record) {
      if (this.rightIndex(col, index)) {
        if (
          dataFromRow.ColumnsEdited !== undefined &&
          dataFromRow.ColumnsEdited.includes(col)
        ) {
          result = ColorCodeModel.PALE_GREEN;
        } else {
          result = ColorCodeModel.PALE_YELLOW;
        }
      }
    }
    return this.sanitizer.bypassSecurityTrustStyle(result);
  }

  private rightIndex(columnName: string, index: number): boolean {
    const COL_INDICES = {
      machineProductId: 0,
      laborWarrantyDays: 1,
      partWarrantyDays: 2
    };

    if (COL_INDICES[columnName] === index) {
      return true;
    } else {
      return false;
    }
  }

  // Start of in cell editing functions
  public cellClickHandler({
    sender,
    rowIndex,
    column,
    columnIndex,
    dataItem,
    isEdited
  }) {
    if (!isEdited && this.isWriteOnly(column.field, dataItem.Column)) {
      sender.editCell(rowIndex, columnIndex, this.createFormGroup(dataItem));
    }
  }

  public cellCloseHandler(args: any): void {
    const { formGroup, dataItem } = args;

    if (
      formGroup.controls['LaborWarrantyDays'].value > 0 &&
      (formGroup.controls['PartWarrantyDays'].value === undefined ||
        formGroup.controls['PartWarrantyDays'].value == 0)
    ) {
      formGroup.controls.PartWarrantyDays.setValue('0');
      formGroup.controls['PartWarrantyDays'].markAsTouched();
      formGroup.controls['PartWarrantyDays'].markAsDirty();
    } else if (
      formGroup.controls['PartWarrantyDays'].value > 0 &&
      (formGroup.controls['LaborWarrantyDays'].value === undefined ||
        formGroup.controls['LaborWarrantyDays'].value == 0)
    ) {
      formGroup.controls.LaborWarrantyDays.setValue('0');
      formGroup.controls['LaborWarrantyDays'].markAsTouched();
      formGroup.controls['LaborWarrantyDays'].markAsDirty();
    }

    if (!formGroup.valid) {
      // prevent closing the edited cell if there are invalid values.
      args.preventDefault();
    } else if (formGroup.dirty) {
      if (dataItem.ColumnsEdited === undefined) {
        dataItem.ColumnsEdited = [];
      }
      if (
        !dataItem.ColumnsEdited.includes(
          args.column.field.charAt(0).toLowerCase() + args.column.field.slice(1)
        )
      ) {
        dataItem.ColumnsEdited.push(
          args.column.field.charAt(0).toLowerCase() + args.column.field.slice(1)
        );
      }

      dataItem.Column.forEach(val => {
        if (
          val.toLowerCase() == 'laborwarrantydays' &&
          dataItem.ColumnsEdited &&
          !dataItem.ColumnsEdited.toString()
            .toLowerCase()
            .includes('laborwarrantydays')
        ) {
          dataItem.ColumnsEdited.push('laborWarrantyDays');
        }
        if (
          val.toLowerCase() == 'partwarrantydays' &&
          dataItem.ColumnsEdited &&
          !dataItem.ColumnsEdited.toString()
            .toLowerCase()
            .includes('partwarrantydays')
        ) {
          dataItem.ColumnsEdited.push('partWarrantyDays');
        }
      });

      this.assignValues(dataItem, formGroup.value);
      this.update(dataItem, args.rowIndex);
    }
  }

  public hasChanges(): boolean {
    return Boolean(this.updatedItems.length || this.createdItems.length);
  }

  public assignValues(target: any, source: any): void {
    Object.assign(target, source);
  }

  public update(item: any, index): void {
    let x = this.updatedItems.length;
    let modifiedExistingRow = false;
    item.rowIndex = index;
    // check if row has already been modified then replace existing object in updated items array
    for (let i = 0; i < x; i++) {
      if (index === this.updatedItems[i].rowIndex) {
        this.updatedItems[i] = item;
        modifiedExistingRow = true;
        break;
      }
    }
    if (modifiedExistingRow === false) {
      this.updatedItems.push(item);
    }
  }

  public saveHandler({ sender, formGroup, rowIndex }): void {
    if (formGroup.valid) {
      sender.closeRow(rowIndex);
    }
  }

  public saveChanges(grid: any): void {
    grid.closeCell();
    grid.cancelCell();
    let rowsCanBeSubmitted = true;
    // add logic to submit data
    for (let t = 0; t < this.updatedItems.length; t++) {
      if (
        !(
          this.updatedItems[t].Column.length ===
          this.updatedItems[t].ColumnsEdited.length
        )
      ) {
        rowsCanBeSubmitted = false;
        break;
      }
    }
    if (rowsCanBeSubmitted) {
      for (let j = 0; j < this.updatedItems.length; j++) {
        for (let i = 0; i < this.errorArr.length; i++) {
          if (
            this.errorArr[i].hasOwnProperty('rowIndex') &&
            this.errorArr[i].rowIndex === this.updatedItems[j].rowIndex
          ) {
            this.errorArr.splice(i, 1);
            break; // so that it doesn't keep looping, after finding a match
          }
        }
      }

      this.errorArrFront = [];
      this.bulkAddWarrantyValidation(this.updatedItems);
      this.updatedItems = [];
    } else {
      this.dialogOpenService.open(PleaseCheckData, []);
    }
  }

  public createFormGroup(dataItem: any): FormGroup {
    return this.formBuilder.group({
      MachineProductId: dataItem.MachineProductId,
      LaborWarrantyDays: dataItem.LaborWarrantyDays,
      PartWarrantyDays: dataItem.PartWarrantyDays
    });
  }

  private isWriteOnly(field: string, columns: any): boolean {
    let writeOnlyColumns = columns.slice();
    for (let i = 0; i < writeOnlyColumns.length; i++) {
      writeOnlyColumns[i] = writeOnlyColumns[i].toUpperCase();
    }
    let fieldStuff = field.toUpperCase();
    let test = writeOnlyColumns.indexOf(fieldStuff);
    return test >= 0;
  }
  // end of in cell editing functions

  public onExportResults(): void {
    let jsonData = this.errorArr.map((row: any) => {
      return {
        MachineProductId: row.MachineProductId,
        LaborWarrantyDays: row.LaborWarrantyDays,
        PartWarrantyDays: row.PartWarrantyDays,
        Issues: row.Issues
      };
    });
    this.exportToExcelService.exportFromJsonToExcel(
      jsonData,
      this.EXCEL_FILE_NAME,
      'xlsx'
    );
  }
}
